import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const L = {
  fr: { cta: "Proposer un sujet / thème", hint: "Une idée, une question, un thème que tu aimerais entendre sur Radio Cocoon ? Propose-le.", name: "Ton nom", email: "Ton email", topic: "Le sujet que tu aimerais explorer…", send: "Envoyer mon sujet", sending: "Envoi…", thanks: "Merci ! Ton sujet est bien reçu — vérifie ta boîte mail.", err: "Oups, réessaie dans un instant.", short: "Décris ton sujet en quelques mots.", cancel: "Annuler" },
  en: { cta: "Suggest a topic / theme", hint: "An idea, a question, a theme you'd love to hear on Radio Cocoon? Suggest it.", name: "Your name", email: "Your email", topic: "The topic you'd like to explore…", send: "Send my topic", sending: "Sending…", thanks: "Thanks! Your topic has been received — check your inbox.", err: "Oops, try again in a moment.", short: "Describe your topic in a few words.", cancel: "Cancel" },
  es: { cta: "Proponer un tema", hint: "¿Una idea, una pregunta, un tema que te gustaría escuchar en Radio Cocoon? Propónlo.", name: "Tu nombre", email: "Tu email", topic: "El tema que te gustaría explorar…", send: "Enviar mi tema", sending: "Enviando…", thanks: "¡Gracias! Hemos recibido tu tema — revisa tu correo.", err: "Vaya, inténtalo de nuevo en un momento.", short: "Describe tu tema en pocas palabras.", cancel: "Cancelar" },
  nl: { cta: "Stel een onderwerp voor", hint: "Een idee, een vraag of thema dat je graag op Radio Cocoon hoort? Stel het voor.", name: "Je naam", email: "Je e-mail", topic: "Het onderwerp dat je wilt verkennen…", send: "Verstuur mijn onderwerp", sending: "Versturen…", thanks: "Bedankt! Je onderwerp is ontvangen — check je inbox.", err: "Oeps, probeer het zo opnieuw.", short: "Beschrijf je onderwerp in een paar woorden.", cancel: "Annuleren" },
};
const INPUT = "rounded-xl bg-background/60 border border-white/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/35 focus:border-primary/50 focus:outline-none";

export const TopicForm = () => {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (topic.trim().length < 3) { toast.error(T.short); return; }
    if (!email.trim()) { toast.error(T.err); return; }
    setBusy(true);
    try {
      await api.post(`/derive/guest`, { name: name.trim(), email: email.trim(), topic: topic.trim(), lang });
      setSent(true);
      toast.success(T.thanks);
    } catch {
      toast.error(T.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 flex flex-col items-center gap-3" data-testid="radio-topic-cta">
      {!open && !sent && (
        <>
          <button onClick={() => setOpen(true)} data-testid="radio-topic-button"
            className="inline-flex items-center gap-2.5 rounded-full bg-primary text-background font-semibold pl-5 pr-6 py-3 text-sm hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(236,198,128,0.3)] transition-[transform,box-shadow]">
            <Mic size={16} /> {T.cta}
          </button>
          <p className="text-foreground/40 text-xs max-w-xs text-center leading-relaxed">{T.hint}</p>
        </>
      )}
      {open && !sent && (
        <form onSubmit={submit} data-testid="radio-topic-form" className="w-full max-w-md rounded-2xl border border-primary/20 bg-white/[0.03] p-5 flex flex-col gap-3 text-left">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm"><Mic size={15} /> {T.cta}</div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={T.name} maxLength={80} data-testid="topic-name-input" className={INPUT} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={T.email} type="email" required data-testid="topic-email-input" className={INPUT} />
          <textarea value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={T.topic} rows={3} required maxLength={1000} data-testid="topic-text-input" className={`${INPUT} resize-none`} />
          <div className="flex items-center gap-2">
            <button type="submit" disabled={busy} data-testid="topic-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-background font-semibold px-5 py-2.5 text-sm disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Mic size={15} />} {busy ? T.sending : T.send}
            </button>
            <button type="button" onClick={() => setOpen(false)} data-testid="topic-cancel-button" className="text-foreground/50 text-sm px-3 py-2 hover:text-foreground/80 transition-colors">{T.cancel}</button>
          </div>
        </form>
      )}
      {sent && (
        <div data-testid="radio-topic-success" className="w-full max-w-md rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4 text-center text-sm text-foreground/90">{T.thanks}</div>
      )}
    </div>
  );
};
